import {AxiosError} from "axios";
import {
    Bin as DeleteIcon,
    CardShield as SMIMEIcon,
    CardShield as CertificateImportIcon,
    Check as TrustIcon,
    DatabaseExport as ExportIcon,
    KeyPlus as KeyImportIcon,
    Refresh as ResetTrustIcon,
    Xmark as DistrustIcon
} from "iconoir-react";
import {Dispatch, SetStateAction, useCallback, useEffect, useState} from 'react'
import {Dropdown, Navbar} from "react-bootstrap";
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Nav from "react-bootstrap/Nav";
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {
    CertificateApi,
    CertificateStore,
    CertificateValidationApi,
    type CertificateValidationResult,
    CTLApi,
    CTLEntryStatus,
    Expired,
    Match,
    MissingKeyAlias,
    type X509CertificateDetails
} from "../api/admin/generated";
import {ActionsDropdown} from "../common/ActionsDropdown";
import {ClearSelection} from "../common/ClearSelection";
import {ConfirmDialog} from "../common/ConfirmDialog";
import {DialogCloseCause} from "../common/Enums";
import {InvertSelection} from "../common/InvertSelection";
import {Pager} from "../common/Pager";
import {PageTitle} from "../common/PageTitle";
import {ReloadButton} from "../common/ReloadButton";
import {RenderAlert} from "../common/RenderAlert";
import {RowsPerPage} from "../common/RowsPerPage";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {logger} from "../utils/Logger";
import {Utils} from "../utils/Utils";
import {CertificateExportDialog} from "./CertificateExportDialog";
import {CertificateFilter} from "./CertificateFilter";
import {CertificateImportDialog} from "./CertificateImportDialog";
import {CertificateRow} from "./CertificateRow";
import {CertificateTableHeader} from "./CertificateTableHeader";
import {PrivateKeyImportDialog} from "./PrivateKeyImportDialog";
import {SearchField} from "./SearchField";

/*
 * REST api for accessing the back-end
 */
const certificateApi = new CertificateApi(ApiConfig.config());
const certificateValidationApi = new CertificateValidationApi(ApiConfig.config());
const ctlApi = new CTLApi(ApiConfig.config());

/**
 * Certificates class represents a component that manages certificates.
 * It provides methods to load, display import and export certificates, as well as delete selected certificates.
 * Certificates from the Certificates store (end-user and intermediate) and Root store can be
 * managed.
 */
export const Certificates = () => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * Represents the current location object in a React Router application.
     */
    const location = useLocation()
    /**
     * The loaded certificates
     */
    const [certificates, setCertificates] = useState<X509CertificateDetails[]>([]);
    /**
     * The total number of certificates
     */
    const [certificateCount, setCertificateCount] = useState<number>(0);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The index of the current page
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of certificates to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * The selected certificates
     */
    const [selectedCertificates, setSelectedCertificates] = useState<X509CertificateDetails[]>([]);
    /**
     * If true, the "confirm deletion" modal dialog will be shown
     */
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    /**
     * If true, the export certificates dialog will be shown
     */
    const [showCertificateExportDialog, setShowCertificateExportDialog] = useState<boolean>(false);
    /**
     * If true, the import certificates dialog will be shown
     */
    const [showCertificateImportDialog, setShowCertificateImportDialog] = useState<boolean>(false);
    /**
     * If true, the import private keys dialog will be shown
     */
    const [showPrivateKeyImportDialog, setShowPrivateKeyImportDialog] = useState<boolean>(false);
    /**
     * If true, the certificates will be reloaded from the back-end
     */
    const [forceReload, setForceReload] = useState<boolean>(false);
    /**
     * The active certificate store (certificates or root)
     */
    const [certificateStore, setCertificateStore] = useState<CertificateStore>(location?.state?.certificateStore??CertificateStore.Certificates);
    /**
     * Determines whether expired certificates are shown
     */
    const [expired, setExpired] = useState<Expired>(location?.state?.expired??Expired.All);
    /**
     * Determines whether certificates without private keys are shown
     */
    const [missingKeyAlias, setMissingKeyAlias] = useState<MissingKeyAlias>(location?.state?.missingKeyAlias??MissingKeyAlias.Allowed);
    /**
     * The field to search on
     */
    const [searchField, setSearchField] = useState<SearchField>(SearchField.Email);
    /**
     * The field which is searched on
     */
    const [activeSearchValue, setActiveSearchValue] = useState<string>();
    /**
     * The field of the search box. This value will be copied to the activeSearchValue when apply is clicked
     */
    const [searchValue, setSearchValue] = useState<string>();
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    useEffect(() => {
        logger.debug(`Getting certificates count from ${certificateStore}`)

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // reset state when we load new data
        setForceReload(false);

        let loadCertificateCountCallback;

        if ((activeSearchValue??"").length > 0)
        {
            switch (searchField)
            {
                case SearchField.Email:
                    loadCertificateCountCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesByEmailCount(
                            certificateStore, `%${activeSearchValue??""}%`, Match.Like, expired, missingKeyAlias);
                    };
                    break;
                case SearchField.Subject:
                    loadCertificateCountCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesBySubjectCount(
                            certificateStore, `%${activeSearchValue??""}%`, expired, missingKeyAlias);
                    };
                    break;
                case SearchField.Issuer:
                    loadCertificateCountCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesByIssuerCount(
                            certificateStore, `%${activeSearchValue??""}%`, expired, missingKeyAlias);
                    };
                    break;
            }
        }
        else {
            loadCertificateCountCallback = async () => {
                return await certificateApi.certificateControllerGetCertificatesCount(certificateStore, expired, missingKeyAlias);
            };
        }

        loadCertificateCountCallback()
            .then(response => {
                setCertificateCount(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting certificates count", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [certificateStore,
        expired,
        missingKeyAlias,
        activeSearchValue,
        searchField,
        navigate,
        setSpinnerStateCounter,
        forceReload,
        t
    ]);

    useEffect(() => {
        logger.debug(`Getting certificates from ${certificateStore}`)

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // reset state when we load new data
        setSelectedCertificates([]);
        setForceReload(false);
        // Note: do not reset errorMessage because otherwise the message might get lost when re-rendering
        //       The error message can be closed by clicking the close icon

        let loadCertificatesCallback;

        if ((activeSearchValue??"").length > 0)
        {
            switch (searchField)
            {
                case SearchField.Email:
                    loadCertificatesCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesByEmail(
                            certificateStore, `%${activeSearchValue??""}%`, Match.Like, expired, missingKeyAlias,
                            (currentPage - 1) * rowsPerPage, rowsPerPage);
                    };
                    break;
                case SearchField.Subject:
                    loadCertificatesCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesBySubject(
                            certificateStore, `%${activeSearchValue??""}%`, expired, missingKeyAlias,
                            (currentPage - 1) * rowsPerPage, rowsPerPage);
                    };
                    break;
                case SearchField.Issuer:
                    loadCertificatesCallback = async () => {
                        return await certificateApi.certificateControllerSearchForCertificatesByIssuer(
                            certificateStore, `%${activeSearchValue??""}%`, expired, missingKeyAlias,
                            (currentPage - 1) * rowsPerPage, rowsPerPage);
                    };
                    break;
            }
        }
        else {
            loadCertificatesCallback = async () => {
                return await certificateApi.certificateControllerGetCertificates(certificateStore, expired,
                    missingKeyAlias, (currentPage - 1) * rowsPerPage, rowsPerPage);
            };
        }

        loadCertificatesCallback().then(response => {
                setCertificates(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting certificates", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [certificateStore,
        expired,
        missingKeyAlias,
        activeSearchValue,
        searchField,
        navigate,
        setSpinnerStateCounter,
        forceReload,
        currentPage,
        rowsPerPage,
        t
    ]);

    const deleteSelected = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        certificateApi.certificateControllerDeleteCertificates(certificateStore, selectedCertificates.map(cert => cert.thumbprint))
            .then((response) => {
                // check if there are certificate which could not be deleted because there are in use
                const notDeletedThumbprints : string [] = response.data;

                if (notDeletedThumbprints.length > 0)
                {
                    setErrorMessage(notDeletedThumbprints.length > 1 ?
                        t("certificates.some-certificates-not-deleted", {thumbprints: notDeletedThumbprints.join(", ")})
                        :
                        t("certificates.certificate-not-deleted", {thumbprints: notDeletedThumbprints.join(", ")}))
                }

                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error deleting certificates", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
                setShowDeleteConfirm(false);
                setSelectedCertificates([]);
            })
    }

    /**
     * Called when a certificate is selected (i.e., when the checkbox is checked)
     */
    const onSelectCertificate = (checked : boolean, certificate : X509CertificateDetails) =>
    {
        setSelectedCertificates(checked ?
            [...selectedCertificates, certificate] :
            selectedCertificates.filter(e => e.thumbprint !== certificate.thumbprint)
        );
    }

    /**
     * Inverts the selection of certificates by updating the list of selected certificates.
     * Each previously unselected certificate will be selected, and each previously
     * selected certificate will be unselected.
     */
    const invertSelection = () => {
        setSelectedCertificates([...certificates].filter(cert => !selectedCertificates.includes(cert)));
    }

    /**
     * Checks the validity of a certificate using the certificate validation API.
     *
     * NOTE: this must be wrapped in a useCallback call because the method is provided to CertificateRow
     */
    const checkCertificateValidity = (useCallback((certificate : X509CertificateDetails, setCertificateValidationResult : Dispatch<SetStateAction<CertificateValidationResult | undefined>>) =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        certificateValidationApi.certificateValidationControllerValidateCertificate(certificateStore, certificate.thumbprint)
            .then((response) =>
            {
                const validationResult : CertificateValidationResult = response.data;

                setCertificateValidationResult(validationResult);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error certificate validation result", err);
                setCertificateValidationResult(undefined);
            }).finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [certificateStore,
        setSpinnerStateCounter]));

    /**
     * Is called when the ExportCertificatesDialog is closed
     */
    const onExportCertificatesDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowCertificateExportDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            // clear the selected certificates after export
            setSelectedCertificates([]);
        }
    }

    /**
     * Is called when the CertificateImportDialog is closed
     */
    const onCertificateImportDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowCertificateImportDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            setForceReload(true);
        }
    }

    /**
     * Called when the PrivateKeyImportDialog is closed
     */
    const onPrivateKeyImportDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowPrivateKeyImportDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            setForceReload(true);
        }
    }

    /**
     * Called when the Certificate store select changes
     */
    const onSelectCertificateStore = (certificateStore : CertificateStore) =>
    {
        // clear state
        // this is needed because otherwise certificates loaded from a different store might be validated
        // until the next render which results in back-end warnings about non-existing certificates
        setCertificates([]);
        setCertificateCount(0);
        setSelectedCertificates([]);

        setCertificateStore(certificateStore);
    }

    const trustSelectedCertificate = (status : CTLEntryStatus) =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        ctlApi.cTLControllerSetCTLs(status, false, selectedCertificates.map(c => c.thumbprint))
            .then(() =>
            {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error setting certificate trust", err);
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
            setSelectedCertificates([]);
        })
    }

    const resetTrustSelectedCertificate = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        ctlApi.cTLControllerDeleteCTLs(selectedCertificates.map(c => c.thumbprint))
            .then(() =>
            {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error resetting certificate trust", err);
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
            setSelectedCertificates([]);
        })
    }

    return (
        <>
            <ConfirmDialog title={t("certificates.confirm")}
                           bodyText={t("certificates.body-text")}
                           confirmButtonTitle={t("certificates.delete")}
                           onClickConfirmEvent={deleteSelected}
                           showConfirm={showDeleteConfirm}
                           setShowConfirm={setShowDeleteConfirm}
            />

            {showCertificateExportDialog && <CertificateExportDialog certificateStore={certificateStore}
                                                                     certificates={selectedCertificates}
                                                                     onClose={onExportCertificatesDialogClose} />}

            {showCertificateImportDialog && <CertificateImportDialog certificateStore={certificateStore}
                                                                     onClose={onCertificateImportDialogClose} />}

            {showPrivateKeyImportDialog && <PrivateKeyImportDialog certificateStore={certificateStore}
                                                                   onClose={onPrivateKeyImportDialogClose} />}

            <Row>
                <div className="d-flex flex-row align-items-center border-bottom pb-2 mb-4">
                    <div className="d-flex justify-content-start">
                        <PageTitle title={t("certificates.title")} icon={<SMIMEIcon />}/>
                    </div>
                    <div className="d-flex flex-fill justify-content-end">
                        <ReloadButton onClickReload={() => setForceReload(true)} />
                    </div>
                </div>
            </Row>

            <Row>
                <Col>
                    <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                </Col>
            </Row>

            <Row>
                <div className="d-flex flex-row align-items-center mb-4">
                    <div className="d-flex justify-content-start">
                        <Form.Select
                                     aria-label={t("certificates.certificate-store")}
                                     value={certificateStore}
                                     onChange={(e) => onSelectCertificateStore(Utils.stringToEnum(CertificateStore, e.target.value as CertificateStore))}>
                            <option value="CERTIFICATES">{t("certificates.certificates")}</option>
                            <option value="ROOTS">{t("certificates.roots")}</option>
                        </Form.Select>
                    </div>
                    <div className="d-flex flex-fill justify-content-end align-items-center">
                        <div className="d-xl-flex">
                            <CertificateFilter expired={expired}
                                               setExpired={setExpired}
                                               missingKeyAlias={missingKeyAlias}
                                               setMissingKeyAlias={setMissingKeyAlias}
                                               searchField={searchField}
                                               setSearchField={setSearchField}
                                               searchValue={searchValue}
                                               setSearchValue={setSearchValue}
                                               activeSearchValue={activeSearchValue}
                                               setActiveSearchValue={setActiveSearchValue}
                            />
                        </div>

                        <div className="ms-4"/>

                        <ActionsDropdown>
                            <Dropdown.Item onClick={() => setShowCertificateImportDialog(true)}>
                                <CertificateImportIcon className="me-2"/> {t("certificates.import-certificates")}
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => setShowPrivateKeyImportDialog(true)}>
                                <KeyImportIcon className="me-2"/>  {t("certificates.import-private-keys")}
                            </Dropdown.Item>
                        </ActionsDropdown>
                    </div>
                </div>
            </Row>

            <Row className="h-100 w-100 overflow-auto">
                <Col>
                    <Table className="sticky sticky-x" bordered hover>
                        {/*Hide the default table header and show the selection menu if certificates are selected*/}
                        {selectedCertificates.length === 0 ?
                            <CertificateTableHeader>
                                {/* Add icon to first column which will invert selection when clicked */}
                                <InvertSelection invertSelectionEvent={invertSelection}/>
                            </CertificateTableHeader>
                            :
                            <thead className="sticky-top">
                                <tr className="text-nowrap">
                                    <th colSpan={15}>
                                        <Navbar className="bg-body-tertiary ps-2">
                                            {/* Add icon to first column which will clear the selection when clicked */}
                                            <ClearSelection clearSelectionEvent={() => setSelectedCertificates([])} checked={true}/>

                                            <Navbar.Text className="ms-2">{selectedCertificates.length} selected</Navbar.Text>

                                            <div className="vr ms-4 me-2"></div>

                                            <Nav>
                                                <Nav.Link onClick={() => setShowDeleteConfirm(true)}><DeleteIcon
                                                    className="text-danger"/> {t("certificates.delete-selected")}
                                                </Nav.Link>
                                                <Nav.Link
                                                    onClick={() => setShowCertificateExportDialog(true)}><ExportIcon
                                                    className="text-success"/> {t("certificates.export-selected")}
                                                </Nav.Link>
                                                <Nav.Link
                                                    onClick={() => trustSelectedCertificate(CTLEntryStatus.Whitelisted)}><TrustIcon
                                                    className="text-success"/> {t("certificates.trust-selected")}
                                                </Nav.Link>
                                                <Nav.Link
                                                    onClick={() => trustSelectedCertificate(CTLEntryStatus.Blacklisted)}><DistrustIcon
                                                    className="text-danger"/> {t("certificates.distrust-selected")}
                                                </Nav.Link>
                                                <Nav.Link
                                                    onClick={() => resetTrustSelectedCertificate()}><ResetTrustIcon
                                                    className="text-success"/> {t("certificates.reset-selected-trust")}
                                                </Nav.Link>
                                            </Nav>
                                        </Navbar>
                                    </th>
                                </tr>
                            </thead>
                        }

                        <tbody>
                        {certificates.map((certificate) => (
                            <CertificateRow key={certificate.thumbprint} certificate={certificate}
                                            checkCertificateValidityCallback={checkCertificateValidity}
                                            selected={selectedCertificates.find((scert) =>  scert.thumbprint === certificate.thumbprint) !== undefined}
                            >
                                {/* Add selection checkbox to first column */}
                                <Form.Check
                                    type="checkbox"
                                    className="me-0"
                                    checked={selectedCertificates.find((scert) =>  scert.thumbprint === certificate.thumbprint) !== undefined}
                                    onChange={(e) => onSelectCertificate(e.target.checked, certificate)}
                                    inline
                                />
                            </CertificateRow>
                        ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>

            <Row className="mt-4 align-items-baseline">
                <div className="col-4 d-flex">
                    <div className="d-flex justify-content-start align-items-center d-none d-md-inline-flex me-4">
                        {certificateCount} {t("common.results")}
                    </div>

                    <div className="d-flex justify-content-start align-items-center">
                        <RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage}
                                     setCurrentPage={setCurrentPage}/>
                    </div>
                </div>
                <div className="col-4">
                    <div className="d-flex justify-content-center">
                        <Pager itemCount={certificateCount}
                               currentPage={currentPage}
                               setCurrentPage={setCurrentPage}
                               maxItemsPerPage={rowsPerPage}
                        />
                    </div>
                </div>
            </Row>
        </>
    );
}
